import { useEffect, useState, useCallback } from "react";
import { Mail, Send, Loader2, Settings2, Users } from "lucide-react";
import { api, useAuth, formatApiError, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

export default function Rappels() {
  const { user } = useAuth();
  const readOnly = !canDo(user, "rappels", "configurer");
  const [personnes, setPersonnes] = useState([]);
  const [selected, setSelected] = useState([]);
  const [destinataires, setDestinataires] = useState("");
  const [sujet, setSujet] = useState("Rappel : certifications SST / SSIAP à recycler");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sstMois, setSstMois] = useState("6, 4, 2, 1");
  const [ssiapMois, setSsiapMois] = useState("8, 6, 4, 2");
  const [carteProMois, setCarteProMois] = useState("12");
  const [delaiSst, setDelaiSst] = useState("6");
  const [delaiSsiap, setDelaiSsiap] = useState("8");
  const [delaiCartePro, setDelaiCartePro] = useState("12");
  const [incendieMois, setIncendieMois] = useState("6, 3, 1");
  const [destIncendie, setDestIncendie] = useState("sean_gerlier@carrefour.com");
  const [delaiIncendie, setDelaiIncendie] = useState("6");
  const [validiteIncendie, setValiditeIncendie] = useState("36");
  const [destSst, setDestSst] = useState("sean_gerlier@carrefour.com");
  const [destSsiap, setDestSsiap] = useState("sean_gerlier@carrefour.com");
  const [destCartePro, setDestCartePro] = useState("sean_gerlier@carrefour.com");
  const [savingSettings, setSavingSettings] = useState(false);

  const buildMessage = useCallback((sel) => {
    const lignes = sel.map((p) => `- ${p.label}`).join("\n");
    return `Bonjour,\n\nRappel : les certifications suivantes arrivent à expiration et doivent être recyclées :\n\n${lignes || "- (sélectionnez des personnes ci-dessus)"}\n\nMerci de me confirmer la bonne réception et de planifier les recyclages.\n\nCordialement,\nSean Gerlier\nFormateur SST / Référent SSIAP`;
  }, []);

  useEffect(() => {
    Promise.all([api.get("/employees?statut=a_recycler"), api.get("/ssiap"), api.get("/cartes-pro"), api.get("/formations-incendie"), api.get("/settings/rappels")])
      .then(([emps, ssiap, cartes, incendie, cfg]) => {
        const liste = [];
        for (const e of emps.data.employees) {
          liste.push({ key: `sst-${e.id}`, label: `${e.prenom} ${e.nom} (${e.service}) — SST, fin de validité ${e.fin_validite} (${e.jours_restants} jours restants)`, categorie: "SST" });
        }
        for (const a of ssiap.data.agents) {
          for (const s of a.ssiap) {
            if (s.statut === "a_recycler") {
              liste.push({ key: `ssiap-${a.id}-${s.hid}`, label: `${a.prenom} ${a.nom} (${a.service}) — SSIAP ${s.niveau}, renouvellement ${s.date_renouvellement} (${s.jours_restants} jours restants)`, categorie: "SSIAP" });
            }
          }
        }
        for (const a of cartes.data.agents) {
          const cp = a.carte_pro;
          if (cp && cp.statut === "a_recycler") {
            liste.push({ key: `cp-${a.id}`, label: `${a.prenom} ${a.nom} (${a.service}) — Carte Pro n° ${cp.nub || "?"}, expiration ${cp.date_validite} (${cp.jours_restants} jours restants)`, categorie: "CARTE PRO" });
          }
        }
        for (const a of incendie.data.agents) {
          const fi = a.formation_incendie;
          if (fi && fi.statut === "a_recycler") {
            liste.push({ key: `fi-${a.id}`, label: `${a.prenom} ${a.nom} (${a.service}) — Formation incendie, expiration ${fi.date_validite} (${fi.jours_restants} jours restants)`, categorie: "INCENDIE" });
          }
        }
        setPersonnes(liste);
        if (cfg.data.sst) setSstMois(cfg.data.sst.map((d) => Math.round(d / 30)).join(", "));
        if (cfg.data.ssiap) setSsiapMois(cfg.data.ssiap.map((d) => Math.round(d / 30)).join(", "));
        if (cfg.data.carte_pro) setCarteProMois(cfg.data.carte_pro.map((d) => Math.round(d / 30)).join(", "));
        if (cfg.data.recyclage) {
          setDelaiSst(String(Math.round((cfg.data.recyclage.sst || 180) / 30)));
          setDelaiSsiap(String(Math.round((cfg.data.recyclage.ssiap || 240) / 30)));
          setDelaiCartePro(String(Math.round((cfg.data.recyclage.carte_pro || 365) / 30)));
          setDelaiIncendie(String(Math.round((cfg.data.recyclage.incendie || 180) / 30)));
        }
        if (cfg.data.incendie) setIncendieMois(cfg.data.incendie.map((d) => Math.round(d / 30)).join(", "));
        if (cfg.data.validite_incendie_mois) setValiditeIncendie(String(cfg.data.validite_incendie_mois));
        if (cfg.data.destinataires) {
          setDestSst((cfg.data.destinataires.sst || []).join(", "));
          setDestSsiap((cfg.data.destinataires.ssiap || []).join(", "));
          setDestCartePro((cfg.data.destinataires.carte_pro || []).join(", "));
          setDestIncendie((cfg.data.destinataires.incendie || []).join(", "));
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    const sel = personnes.filter((p) => selected.includes(p.key));
    setMessage(buildMessage(sel));
  }, [selected, personnes, buildMessage]);

  const toggle = (key) => setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const send = async () => {
    const dests = destinataires.split(/[,;\s]+/).filter(Boolean);
    if (!dests.length) {
      toast.error("Saisissez au moins un destinataire");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/rappels/envoyer", { destinataires: dests, sujet, message });
      toast.success(`Rappel envoyé à ${data.envoyes.length} destinataire(s)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const saveSettings = async () => {
    const parse = (s) => s.split(/[,;\s]+/).filter(Boolean).map((x) => parseInt(x) * 30).filter((x) => !isNaN(x));
    setSavingSettings(true);
    try {
      const emails = (s) => s.split(/[,;\s]+/).filter(Boolean);
      await api.put("/settings/rappels", {
        sst: parse(sstMois), ssiap: parse(ssiapMois), carte_pro: parse(carteProMois), incendie: parse(incendieMois),
        recyclage: { sst: (parseInt(delaiSst) || 6) * 30, ssiap: (parseInt(delaiSsiap) || 8) * 30, carte_pro: (parseInt(delaiCartePro) || 12) * 30, incendie: (parseInt(delaiIncendie) || 6) * 30 },
        validite_incendie_mois: parseInt(validiteIncendie) || 36,
        destinataires: { sst: emails(destSst), ssiap: emails(destSsiap), carte_pro: emails(destCartePro), incendie: emails(destIncendie) },
      });
      toast.success("Seuils de rappels enregistrés");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="rappels-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Notifications</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Rappels email</h1>
        <p className="text-sm text-muted-foreground mt-1">Envoyez un rappel manuel au(x) destinataire(s) de votre choix, et réglez les seuils des rappels automatiques.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Users className="h-4 w-4 text-accent" /> Personnes à recycler
            </h2>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border/50" data-testid="rappels-personnes-list">
              {personnes.length === 0 && <p className="px-3 py-6 text-sm text-muted-foreground text-center">Aucune personne à recycler actuellement.</p>}
              {personnes.map((p) => (
                <label key={p.key} className="flex items-start gap-3 px-3 py-2.5 hover:bg-secondary/40 cursor-pointer">
                  <Checkbox checked={selected.includes(p.key)} onCheckedChange={() => toggle(p.key)} data-testid={`rappel-check-${p.key}`} className="mt-0.5" />
                  <span className="text-sm">
                    {p.categorie === "SSIAP" ? (
                      <span className="mr-1.5 inline-flex rounded-full border border-red-700 bg-red-950 px-1.5 py-0.5 text-[10px] font-bold text-red-400">SSIAP</span>
                    ) : p.categorie === "CARTE PRO" ? (
                      <span className="mr-1.5 inline-flex rounded-full border border-orange-700 bg-orange-950 px-1.5 py-0.5 text-[10px] font-bold text-orange-400">CARTE PRO</span>
                    ) : p.categorie === "INCENDIE" ? (
                      <span className="mr-1.5 inline-flex rounded-full border border-sky-700 bg-sky-950 px-1.5 py-0.5 text-[10px] font-bold text-sky-400">INCENDIE</span>
                    ) : (
                      <span className="mr-1.5 inline-flex rounded-full border border-emerald-700 bg-emerald-950 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400">SST</span>
                    )}
                    {p.label}
                  </span>
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Destinataire(s) * <span className="text-muted-foreground text-xs">(séparés par des virgules)</span></Label>
              <Input value={destinataires} onChange={(e) => setDestinataires(e.target.value)} placeholder="ex : responsable@carrefour.com, autre@exemple.fr"
                className="bg-background" data-testid="rappels-destinataires" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input value={sujet} onChange={(e) => setSujet(e.target.value)} className="bg-background" data-testid="rappels-sujet" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Message <span className="text-muted-foreground text-xs">(modifiable avant envoi)</span></Label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={10} disabled={readOnly}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="rappels-message" />
            </div>
            {!readOnly && (
              <Button onClick={send} disabled={sending || !selected.length} data-testid="rappels-send-button">
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Envoyer le rappel ({selected.length} personne(s))
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-3" style={{ fontFamily: "Outfit" }}>
              <Mail className="h-4 w-4 text-accent" /> Aperçu du message
            </h2>
            <div className="rounded-lg overflow-hidden border border-border" data-testid="rappels-preview">
              <div className="bg-[#0B0F17] px-4 py-3">
                <span className="text-white font-bold text-sm">SUIVI DES CERTIFICATIONS / FORMATIONS</span>
                <span className="ml-2 text-[10px] uppercase tracking-widest text-teal-400">Suivi des certifications</span>
              </div>
              <div className="bg-white p-4">
                <p className="text-[#111827] text-sm font-semibold mb-3">{sujet}</p>
                <p className="text-[#374151] text-sm whitespace-pre-wrap">{message}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Settings2 className="h-4 w-4 text-accent" /> Rappels automatiques
            </h2>
            <p className="text-xs text-muted-foreground">
              Envoyés automatiquement chaque matin (6h30) quand une certification atteint un seuil — chaque type part vers la/les adresse(s) de votre choix.
            </p>
            <div className="space-y-2">
              <Label>Destinataires des rappels <span className="text-emerald-500 font-semibold">SST</span></Label>
              <Input value={destSst} onChange={(e) => setDestSst(e.target.value)} placeholder="email1@exemple.fr, email2@exemple.fr" className="bg-background font-mono text-xs" data-testid="settings-dest-sst" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Destinataires des rappels <span className="text-red-500 font-semibold">SSIAP</span></Label>
              <Input value={destSsiap} onChange={(e) => setDestSsiap(e.target.value)} placeholder="email1@exemple.fr" className="bg-background font-mono text-xs" data-testid="settings-dest-ssiap" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Destinataires des rappels <span className="text-orange-500 font-semibold">Cartes Pro</span></Label>
              <Input value={destCartePro} onChange={(e) => setDestCartePro(e.target.value)} placeholder="email1@exemple.fr" className="bg-background font-mono text-xs" data-testid="settings-dest-cartepro" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Destinataires des rappels <span className="text-sky-500 font-semibold">Formations Incendie</span></Label>
              <Input value={destIncendie} onChange={(e) => setDestIncendie(e.target.value)} placeholder="email1@exemple.fr" className="bg-background font-mono text-xs" data-testid="settings-dest-incendie" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Rappels SST (mois avant expiration, séparés par des virgules)</Label>
              <Input value={sstMois} onChange={(e) => setSstMois(e.target.value)} className="bg-background font-mono" data-testid="settings-sst-mois" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Rappels SSIAP (mois avant expiration)</Label>
              <Input value={ssiapMois} onChange={(e) => setSsiapMois(e.target.value)} className="bg-background font-mono" data-testid="settings-ssiap-mois" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Rappels Cartes Pro (mois avant expiration)</Label>
              <Input value={carteProMois} onChange={(e) => setCarteProMois(e.target.value)} className="bg-background font-mono" data-testid="settings-cartepro-mois" disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Rappels <span className="text-sky-500 font-semibold">Formations Incendie</span> (mois avant expiration)</Label>
              <Input value={incendieMois} onChange={(e) => setIncendieMois(e.target.value)} className="bg-background font-mono" data-testid="settings-incendie-mois" disabled={readOnly} />
            </div>
            <div className="pt-3 border-t border-border space-y-3">
              <p className="text-xs text-muted-foreground">
                Statut « À recycler » : une certification passe en « à recycler » quand sa fin de validité est à moins de ce délai (en mois).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>Statut « à recycler » <span className="text-emerald-500 font-semibold">SST</span> dès (mois)</Label>
                  <Input type="number" min="1" max="24" value={delaiSst} onChange={(e) => setDelaiSst(e.target.value)} className="bg-background font-mono" data-testid="settings-delai-sst" disabled={readOnly} />
                </div>
                <div className="space-y-2">
                  <Label>Statut « à recycler » <span className="text-red-500 font-semibold">SSIAP</span> dès (mois)</Label>
                  <Input type="number" min="1" max="24" value={delaiSsiap} onChange={(e) => setDelaiSsiap(e.target.value)} className="bg-background font-mono" data-testid="settings-delai-ssiap" disabled={readOnly} />
                </div>
                <div className="space-y-2">
                  <Label>Statut « à recycler » <span className="text-orange-500 font-semibold">Carte Pro</span> dès (mois)</Label>
                  <Input type="number" min="1" max="24" value={delaiCartePro} onChange={(e) => setDelaiCartePro(e.target.value)} className="bg-background font-mono" data-testid="settings-delai-cartepro" disabled={readOnly} />
                </div>
                <div className="space-y-2">
                  <Label>Statut « à revoir » <span className="text-sky-500 font-semibold">Incendie</span> dès (mois)</Label>
                  <Input type="number" min="1" max="24" value={delaiIncendie} onChange={(e) => setDelaiIncendie(e.target.value)} className="bg-background font-mono" data-testid="settings-delai-incendie" disabled={readOnly} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Validité d'une <span className="text-sky-500 font-semibold">formation incendie</span> (mois)</Label>
                <Input type="number" min="1" max="240" value={validiteIncendie} onChange={(e) => setValiditeIncendie(e.target.value)} className="bg-background font-mono sm:max-w-[200px]" data-testid="settings-validite-incendie" disabled={readOnly} />
                <p className="text-xs text-muted-foreground">Quand vous saisissez une date de formation sans fin de validité, celle-ci est calculée automatiquement (+ cette durée).</p>
              </div>
            </div>
            {!readOnly && (
              <Button variant="outline" onClick={saveSettings} disabled={savingSettings} data-testid="settings-save-button">
                {savingSettings ? "Enregistrement..." : "Enregistrer les seuils"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
